import {
  Card,
  CardContent,
  Divider,
  IconButton,
  Typography,
} from '@material-ui/core';
import { Add, Edit } from '@material-ui/icons';
import { formatDate } from '@superdispatch/dates';
import { formatPaymentTerm } from '@superdispatch/sdk';
import { CardButton, Column, Columns, Inline, Stack } from '@superdispatch/ui';
import { useMemo, useState } from 'react';
import { BankErrorSimpleBanner } from 'shared/errors/SuperPayBankErrors';
import { isEmptyRecord } from 'shared/helpers/CommonHelpers';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import { SuperPayNudgingBanner } from 'shared/modules/superpay/SuperPayNudgingBanner';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { HiddenForPrint } from 'shared/ui/Print';
import { EditPaymentAgreementDrawer } from '../core/EditPaymentAgreementDrawer';
import { castAPIData } from 'shared/api/APISchema';
import { LoadDTO, loadPaymentSchema } from '../data/LoadDTO';
import { ViewLoadLegNotes } from './ViewLoadLegNotes';
import {
  ViewLoadPaymentMethod,
  ViewLoadPaymentOriginalMethod,
} from './ViewLoadPaymentMethod';
import { ViewLoadPaymentCardRow } from './ViewLoadPaymentRenderCardRow';
import { ViewLoadPaymentDue } from './ViewLoadPaymentDue';
import { useSubhaulLoad } from './useSubhaulLoad';
import { Box } from '@superdispatch/ui-lab';
import { EditSubhaulerPaymentAgreementDrawer } from '../core/EditSubhaulerPaymentAgreementDrawer';
import { formatCarrierPaymentMethod } from 'shared/helpers/CarrierPaymentTypes';

interface ViewLoadPaymentAgreementProps {
  load: LoadDTO;
  layout?: 'print';
  isEnabledEditLoad?: boolean;
}

const defaultValuePlaceholder = 'No details';

export function ViewLoadPaymentAgreement({
  load,
  layout,
  isEnabledEditLoad = true,
}: ViewLoadPaymentAgreementProps) {
  const { isSubhauled } = useSubhaulLoad();
  const { data: settings } = useCarrierSettings();

  const payment = useMemo(
    () =>
      load.payments && load.payments.length > 0
        ? castAPIData(loadPaymentSchema, load.payments[0])
        : null,
    [load.payments],
  );

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditSubhaulerPaymentOpen, setIsEditSubhaulerPaymentOpen] =
    useState(false);

  const isEmpty = useMemo(() => {
    if (!payment) return false;
    const {
      invoice,
      price = null,
      payment_details,
      ...paymentDetails
    } = payment;
    return (
      !price &&
      isEmptyRecord(invoice) &&
      isEmptyRecord(paymentDetails) &&
      isEmptyRecord(load.subhauler_payment) &&
      isEmptyRecord(load.customer_payment)
    );
  }, [payment, load.subhauler_payment, load.customer_payment]);

  const isPaymentMethodUpdated =
    payment &&
    payment.original_method != null &&
    load.subhauler_payment?.payment_method !== null &&
    load.subhauler_payment?.payment_method !== payment.original_method &&
    load.payment_details?.payment_method != null &&
    load.payment_details?.payment_method !== payment.original_method;

  const isPriceUpdated =
    payment &&
    payment.original_price != null &&
    payment.price != null &&
    payment.price !== payment.original_price;

  return (
    <>
      <EditPaymentAgreementDrawer
        loadGUID={load.guid}
        open={isDrawerOpen}
        isEnabledEditLoad={isEnabledEditLoad}
        onClose={() => {
          setIsDrawerOpen(false);
        }}
      />

      <EditSubhaulerPaymentAgreementDrawer
        loadGUID={load.guid}
        open={isEditSubhaulerPaymentOpen}
        onClose={() => {
          setIsEditSubhaulerPaymentOpen(false);
        }}
      />

      {isEmpty ? (
        layout !== 'print' && (
          <HiddenForPrint>
            <CardButton
              startIcon={<Add />}
              onClick={() => {
                if (isSubhauled) {
                  setIsEditSubhaulerPaymentOpen(true);
                } else {
                  setIsDrawerOpen(true);
                }
              }}
              aria-label="Add payment agreement"
              disabled={!load.can_be_edited}
            >
              Add payment agreement
            </CardButton>
          </HiddenForPrint>
        )
      ) : (
        <Card
          aria-label="Payment Agreement Details"
          elevation={0}
          variant={layout === 'print' ? 'elevation' : 'outlined'}
        >
          <CardContent>
            <Stack space="large">
              <Stack space="small">
                <Stack>
                  <Columns space="xxsmall">
                    <Column width="fluid">
                      <Stack>
                        <Typography variant="h3" color="textPrimary">
                          Payment Agreement
                        </Typography>
                        {load.payment_details?.payment_method === 'superpay' &&
                          (settings?.superpay.payment_error ? (
                            <BankErrorSimpleBanner
                              source="Load Details"
                              paymentError={settings.superpay.payment_error}
                            />
                          ) : (
                            <SuperPayNudgingBanner source="Load Details" />
                          ))}
                      </Stack>
                    </Column>

                    {layout !== 'print' &&
                      (load.can_be_edited || isSubhauled) && (
                        <Column width="content">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (isSubhauled) {
                                setIsEditSubhaulerPaymentOpen(true);
                              } else {
                                setIsDrawerOpen(true);
                              }
                            }}
                            aria-label="Edit Payment Agreement Button"
                          >
                            <Edit />
                          </IconButton>
                        </Column>
                      )}
                  </Columns>
                </Stack>

                <Stack space="xsmall">
                  <ViewLoadPaymentCardRow
                    title="Price"
                    e2eValue="payment.price"
                    value={
                      (payment &&
                        payment.price &&
                        formatCurrency(payment.price)) ||
                      (load.customer_payment?.price &&
                        formatCurrency(load.customer_payment.price)) ||
                      defaultValuePlaceholder
                    }
                    boldValue={true}
                  />

                  {isPriceUpdated && (
                    <ViewLoadPaymentCardRow
                      title="Original Price"
                      e2eValue="payment.original_price"
                      value={
                        (payment.original_price &&
                          formatCurrency(payment.original_price)) ||
                        defaultValuePlaceholder
                      }
                    />
                  )}

                  {!isSubhauled && !load.is_received_via_subhaul && (
                    <ViewLoadPaymentCardRow
                      title="Broker Fee"
                      e2eValue="payment.broker_fee"
                      value={
                        (payment &&
                          payment.broker_fee &&
                          formatCurrency(payment.broker_fee)) ||
                        defaultValuePlaceholder
                      }
                    />
                  )}

                  <ViewLoadPaymentMethod load={load} />

                  {isPaymentMethodUpdated && (
                    <ViewLoadPaymentOriginalMethod load={load} />
                  )}

                  <ViewLoadPaymentCardRow
                    title="Terms"
                    e2eValue="payment.payment_terms"
                    value={
                      load.customer_payment?.payment_terms
                        ? formatPaymentTerm(load.customer_payment.payment_terms)
                        : load.payment_details?.payment_terms
                          ? formatPaymentTerm(
                              load.payment_details.payment_terms,
                            )
                          : defaultValuePlaceholder
                    }
                  />

                  {payment && payment.payment_due && !isSubhauled && (
                    <ViewLoadPaymentDue
                      title="Payment Due"
                      e2eValue="payment.payment_due"
                      value={formatDate(payment.payment_due, {
                        variant: 'Date',
                      })}
                      paymentStatus={payment.status}
                    />
                  )}

                  {!isSubhauled && (
                    <ViewLoadPaymentCardRow
                      title="Driver Pay"
                      e2eValue="payment.driver_pay"
                      value={
                        (payment &&
                          payment.driver_pay &&
                          formatCurrency(payment.driver_pay)) ||
                        defaultValuePlaceholder
                      }
                    />
                  )}

                  {isSubhauled && (
                    <Box paddingTop="xsmall" paddingBottom="xsmall">
                      <Divider />
                    </Box>
                  )}

                  {isSubhauled && (
                    <Stack space="xxsmall">
                      <ViewLoadPaymentCardRow
                        title="Sub-haul Payout"
                        value={
                          (load.subhauler_payment?.price &&
                            formatCurrency(load.subhauler_payment.price)) ||
                          defaultValuePlaceholder
                        }
                        boldValue={true}
                      />
                      <Inline verticalAlign="center" horizontalAlign="right">
                        <Typography variant="caption" color="textSecondary">
                          {formatCarrierPaymentMethod(
                            load.subhauler_payment?.payment_method,
                          )}{' '}
                          ·{' '}
                          {formatPaymentTerm(
                            load.subhauler_payment?.payment_terms,
                          )}
                        </Typography>
                      </Inline>
                    </Stack>
                  )}
                </Stack>

                {payment && !!payment.notes && (
                  <Stack>
                    <Typography color="textSecondary">Notes</Typography>
                    <ViewLoadLegNotes
                      content={payment.notes}
                      data-e2e="payment.notes"
                      disableTruncate={layout === 'print'}
                    />
                  </Stack>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}
    </>
  );
}
